import React, { useEffect, useRef } from 'react';
import { ROUTES, CHILD_ROUTES } from 'src/constants/route';

const MOCK_WIDGETS = [
  { moduleId: 'module-2', label: 'Mock Video Module' },
  { moduleId: 'module-1', label: 'Mock PDF Module' },
];

const BETA_WIDGETS = [
  { moduleId: '84fea35b-c5c6-4ae3-999b-1cea1b3a6d96', label: 'Beta Video Module' },
  { moduleId: '46982f1c-0fde-4ad0-9d6c-5093711cb982', label: 'Beta PDF Module' },
];

const buildWidgetSrc = (moduleId: string): string => (
  `${ROUTES.WIDGETS.BASE}/${CHILD_ROUTES.LEARNING_MODULE_CARD}/${moduleId}`
);

/**
 * Test page that embeds SU widget cards in iframes to simulate
 * how external pages integrate with our widget endpoint.
 *
 * Route: /su-test/learn/widget-iframe-test
 */
export const handleWidgetMessage = (
  event: MessageEvent,
  container: HTMLDivElement | null,
) => {
  if (!event.data || !event.data.type) return;
  if (event.origin !== window.location.origin) return;
  if (!container) return;

  const iframes = container.querySelectorAll<HTMLIFrameElement>('iframe');

  let iframe: HTMLIFrameElement | null = null;
  iframes.forEach((el) => {
    if (el.contentWindow === event.source) {
      iframe = el;
    }
  });
  if (!iframe) return;

  const target = iframe as HTMLIFrameElement;

  if (event.data.type === 'SU_WIDGET_MODAL_OPEN') {
    target.dataset.origStyle = target.style.cssText;
    target.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:100vw',
      'height:100vh',
      'z-index:2147483647',
      'border:none',
      'border-radius:0',
    ].join(';');
  } else if (event.data.type === 'SU_WIDGET_MODAL_CLOSE') {
    target.style.cssText = target.dataset.origStyle || '';
  }
};

const WidgetIframeTestPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: MessageEvent) => handleWidgetMessage(event, containerRef.current);
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <div ref={containerRef}>
      <h1 className="text-2xl mb-2 font-semibold">
        Widget Iframe Embed Test
      </h1>
      <p className="text-sm text-gray-500 mb-8">
        Simulates how external pages embed our widget endpoint via iframe.
      </p>

      <section className="mb-10">
        <h2 className="text-lg mb-4 font-semibold">Mock Data (local dev)</h2>
        <div className="gap-6 flex flex-wrap">
          {MOCK_WIDGETS.map(({ moduleId, label }) => (
            <div key={moduleId} className="flex w-[500px] flex-col">
              <span className="text-xs text-gray-400 mb-2">{label}</span>
              <iframe
                src={buildWidgetSrc(moduleId)}
                title={label}
                allow="autoplay; fullscreen"
                className="border border-gray-300 bg-white h-[450px] w-full rounded-lg"
              />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg mb-4 font-semibold">Real Modules (beta)</h2>
        <div className="gap-6 flex flex-wrap">
          {BETA_WIDGETS.map(({ moduleId, label }) => (
            <div key={moduleId} className="flex w-[500px] flex-col">
              <span className="text-xs text-gray-400 mb-2">{label}</span>
              <iframe
                src={buildWidgetSrc(moduleId)}
                title={label}
                allow="autoplay; fullscreen"
                className="border border-gray-300 bg-white h-[450px] w-full rounded-lg"
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default WidgetIframeTestPage;
